import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChatDisclaimer } from "@/components/AiNotice";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import * as htmlToImage from "html-to-image";
import {
  ArrowLeft, Send, Loader2, Trash2, PartyPopper, Sparkles, Laugh,
  ThumbsUp, ThumbsDown, Share2, Mail, Sun,
} from "lucide-react";
import { FARFADET_PAGE } from "@/constants/testIds";
import {
  chatFarfadet, chatFarfadetHistory, chatFarfadetClear, chatFarfadetUsage,
  chatFarfadetRate, farfadetDaily,
} from "@/lib/api";
import { useLang } from "@/lib/lang";
import { useAccount } from "@/lib/account";
import { PLAYLISTS, ACCENTS } from "@/lib/tracks";
import MusicPicker from "@/components/MusicPicker";
import VoiceButton from "@/components/VoiceButton";

const SESSION_KEY = "astrovoyance.farfadet_session";

const SUGGESTIONS_FR = [
  "Une punchline sur les rendez-vous Zoom",
  "Une blague absurde sur mon frigo",
  "Rire (tendrement) du lundi matin",
  "Fais-moi une blague qui pince un peu le cœur",
  "Une observation façon Devos sur le silence",
];
const SUGGESTIONS_EN = [
  "A punchline about Zoom meetings",
  "An absurd joke about my fridge",
  "Laugh (gently) about Monday mornings",
  "A joke that pinches the heart a bit",
  "A Devos-style take on silence",
];

export default function Farfadet() {
  const { lang, t, setLang } = useLang();
  const { email, status } = useAccount() || {};
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || "");
  const [ratings, setRatings] = useState({}); // idx -> "up"|"down"
  const [daily, setDaily] = useState(null);
  const [usage, setUsage] = useState({ used_today: 0, limit: null, premium_active: true, remaining: null });
  const boxRef = useRef(null);
  const jokeRefs = useRef({});

  useEffect(() => {
    if (!sessionId) return;
    chatFarfadetHistory(sessionId).then((d) => setMessages(d.messages || [])).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    farfadetDaily(lang, email).then(setDaily).catch(() => {});
  }, [lang, email, status?.premium_active]);

  useEffect(() => {
    chatFarfadetUsage(email).then(setUsage).catch(() => {});
  }, [email, status?.premium_active, messages.length]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages, loading]);

  const rate = useCallback(async (idx, rating, joke) => {
    if (ratings[idx]) return; // already rated
    setRatings((r) => ({ ...r, [idx]: rating }));
    try {
      await chatFarfadetRate(idx, joke.slice(0, 500), rating, sessionId);
      if (rating === "up") toast.success(lang === "fr" ? "🎯 Ça t'a plu — le Farfadet note." : "🎯 You liked it — the Farfadet takes note.");
    } catch {
      /* silently ignore — feedback is opportunistic */
    }
  }, [ratings, sessionId, lang]);

  const shareJoke = useCallback(async (idx, joke) => {
    const el = jokeRefs.current[idx];
    if (!el) return;
    try {
      const dataUrl = await htmlToImage.toPng(el, {
        pixelRatio: 2,
        backgroundColor: "#051610",
        style: { padding: "24px", borderRadius: "16px" },
      });
      // Try native share first (mobile), fallback to download
      if (navigator.share && navigator.canShare) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], `farfadet-punchline.png`, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "Farfadet · AstroVoyance",
            text: lang === "fr" ? "Blague du Farfadet 🍀" : "Farfadet's punchline 🍀" });
          return;
        }
      }
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `farfadet-punchline-${Date.now()}.png`;
      link.click();
      toast.success(lang === "fr" ? "Image sauvegardée." : "Image saved.");
    } catch (err) {
      toast.error(lang === "fr" ? "Impossible de générer l'image." : "Couldn't generate image.");
    }
  }, [lang]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;
    setMessages((m) => [...m, { sender: "user", message: trimmed, created_at: new Date().toISOString() }]);
    setInput("");
    setLoading(true);
    try {
      const r = await chatFarfadet(trimmed, sessionId, email, lang);
      if (r.session_id && r.session_id !== sessionId) {
        setSessionId(r.session_id);
        localStorage.setItem(SESSION_KEY, r.session_id);
      }
      setMessages((m) => [...m, { sender: "advisor", message: r.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.code === "daily_farfadet_limit_reached") {
        toast.error(lang === "fr"
          ? `Limite atteinte (${detail.limit}/jour). Passe Premium pour rire sans limite.`
          : `Daily limit reached (${detail.limit}/day). Go Premium for unlimited laughs.`);
        setUsage((u) => ({ ...u, used_today: detail.used_today, remaining: 0 }));
      } else {
        toast.error(lang === "fr" ? "Le Farfadet est parti dérider ailleurs. Réessaie." : "The Farfadet went cracking jokes elsewhere. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };


  const wipe = async () => {
    try { await chatFarfadetClear(sessionId); } catch { /* ignore */ }
    localStorage.removeItem(SESSION_KEY);
    setSessionId("");
    setMessages([]);
    setRatings({});
    toast.success(lang === "fr" ? "Blagues effacées. Va rire ailleurs." : "Jokes wiped. Go laugh elsewhere.");
  };

  const suggestions = lang === "fr" ? SUGGESTIONS_FR : SUGGESTIONS_EN;

  return (
    <div data-testid={FARFADET_PAGE.page} className="farfadet-root min-h-screen relative">
      <div className="farfadet-bg" aria-hidden="true" />
      <header className="relative z-30">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "#7dd3c0" }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
               style={{ background: "rgba(50,120,80,0.28)", border: "1px solid rgba(180,230,190,0.28)" }}>
            <button onClick={() => setLang("fr")} type="button" className="px-2.5 py-1.5"
                    style={{ background: lang === "fr" ? "#c8f76c" : "transparent", color: lang === "fr" ? "#0a1a10" : "#c8dbcb", fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
            <button onClick={() => setLang("en")} type="button" className="px-2.5 py-1.5"
                    style={{ background: lang === "en" ? "#c8f76c" : "transparent", color: lang === "en" ? "#0a1a10" : "#c8dbcb", fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
          </div>
          <MusicPicker playlist={PLAYLISTS.farfadet} accent={ACCENTS.farfadet} testId="farfadet-music" persona="Farfadet" />
        </div>
      </header>

      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pt-4 pb-8">
        <div className="overline mb-4" style={{ color: "#c8f76c" }}>
          <span className="kicker-line" />
          {lang === "fr" ? "Le Farfadet · gratuit · illimité" : "The Farfadet · free · unlimited"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#f4ead0" }}>
          {lang === "fr" ? (
            <>Le <span className="italic" style={{ color: "#c8f76c" }}>Farfadet</span>, machine à blagues.</>
          ) : (
            <>The <span className="italic" style={{ color: "#c8f76c" }}>Farfadet</span>, joke machine.</>
          )}
        </h1>
        <p className="mt-5 max-w-2xl text-base sm:text-lg" style={{ color: "#c8dbcb" }}>
          {lang === "fr"
            ? "Aucune sagesse. Aucun conseil. Que du rire international qui pique et qui touche. Des one-liners à la Devos, du surréalisme Monty Python, de l'humour noir bien dosé — et de temps en temps une blague qui te fait rire tout en te pinçant le cœur."
            : "No wisdom. No advice. Only international laughter that stings and touches. One-liners à la Devos, Monty Python surrealism, well-dosed dark humor — and now and then a joke that makes you laugh while pinching your heart."}
        </p>

        {/* Usage counter — freemium 20/day */}
        <div data-testid="farfadet-usage"
             className="mt-6 inline-flex items-center gap-3 text-xs font-mono px-4 py-2 rounded-full"
             style={{ background: "rgba(6, 14, 8, 0.65)",
                      border: "1px solid rgba(200, 247, 108, 0.35)",
                      color: "#c8f76c" }}>
          <Laugh size={13} />
          {usage.premium_active
            ? (lang === "fr" ? "Illimité · Toujours gratuit" : "Unlimited · Always free")
            : (
                <>
                  {usage.used_today ?? 0} / {usage.limit ?? 20}{" "}
                  {lang === "fr" ? "blagues aujourd'hui" : "jokes today"}
                </>
              )}
        </div>
      </section>

      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pb-24">
        <div className="farfadet-chat" data-testid={FARFADET_PAGE.chatBox}>
          <div ref={boxRef} className="farfadet-messages">
            {messages.length === 0 && (
              <div className="farfadet-empty">
                <PartyPopper size={22} style={{ color: "#c8f76c" }} />
                <p className="mt-3 font-serif text-xl" style={{ color: "#f4ead0" }}>
                  {lang === "fr" ? "Le Farfadet ajuste son chapeau. Prêt à rire ?" : "The Farfadet adjusts his hat. Ready to laugh?"}
                </p>
                <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "#c8dbcb" }}>
                  {lang === "fr" ? "Ou lance une blague :" : "Or throw a punchline:"}
                </p>
                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                  {suggestions.map((s, i) => (
                    <button key={i} type="button"
                            data-testid={FARFADET_PAGE.suggestion(i)}
                            onClick={() => send(s)}
                            className="farfadet-chip">
                      <Laugh size={11} /> {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`farfadet-msg ${m.sender === "user" ? "user" : "advisor"}`}>
                <div className="farfadet-msg-inner"
                     ref={(el) => { if (m.sender === "advisor" && el) jokeRefs.current[i] = el; }}>
                  {m.sender === "advisor" ? <ReactMarkdown>{m.message}</ReactMarkdown> : <span>{m.message}</span>}
                  {m.sender === "advisor" && (
                    <div className="farfadet-actions">
                      <VoiceButton text={m.message} lang={lang} accent="#c8f76c" testId={`farfadet-voice-${i}`} />
                      <button type="button" onClick={() => rate(i, "up", m.message)}
                              disabled={!!ratings[i]}
                              data-testid={FARFADET_PAGE.rateUp(i)}
                              className={`farfadet-action ${ratings[i] === "up" ? "active-up" : ""}`}
                              title={lang === "fr" ? "Ça m'a fait rire" : "Made me laugh"}>
                        <ThumbsUp size={12} />
                      </button>
                      <button type="button" onClick={() => rate(i, "down", m.message)}
                              disabled={!!ratings[i]}
                              data-testid={FARFADET_PAGE.rateDown(i)}
                              className={`farfadet-action ${ratings[i] === "down" ? "active-down" : ""}`}
                              title={lang === "fr" ? "Bof" : "Meh"}>
                        <ThumbsDown size={12} />
                      </button>
                      <button type="button" onClick={() => shareJoke(i, m.message)}
                              data-testid={FARFADET_PAGE.share(i)}
                              className="farfadet-action"
                              title={lang === "fr" ? "Partager la punchline" : "Share the punchline"}>
                        <Share2 size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="farfadet-msg advisor">
                <div className="farfadet-msg-inner inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  <span className="text-sm" style={{ color: "#c8dbcb" }}>{lang === "fr" ? "Le Farfadet prépare sa chute…" : "Farfadet cooking the punchline…"}</span>
                </div>
              </div>
            )}
          </div>

          <form className="farfadet-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <textarea
              data-testid={FARFADET_PAGE.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={lang === "fr" ? "Lance un sujet, il en fera une blague…" : "Throw a topic — he'll turn it into a joke…"}
              rows={2}
              className="farfadet-textarea"
              disabled={loading}
            />
            <div className="flex items-center gap-2">
              <button type="button" onClick={wipe} data-testid={FARFADET_PAGE.clear} className="farfadet-btn-ghost">
                <Trash2 size={14} />
              </button>
              <button type="submit" disabled={loading || !input.trim()} data-testid={FARFADET_PAGE.send} className="farfadet-btn-primary">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {lang === "fr" ? "Envoyer" : "Send"}
              </button>
            </div>
          </form>
          <ChatDisclaimer testId="farfadet-ai-disclaimer" />
        </div>
      </section>

      <style>{`
        .farfadet-root { background: radial-gradient(ellipse at top, #0e2418 0%, #051610 55%, #030c07 100%); color: #f4ead0; overflow-x: hidden; }
        .farfadet-bg { position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background:
            radial-gradient(circle at 20% 30%, rgba(200,247,108,0.14) 0%, transparent 42%),
            radial-gradient(circle at 80% 70%, rgba(125,211,192,0.14) 0%, transparent 42%),
            linear-gradient(180deg, #0d2118 0%, #04120c 100%); }
        .farfadet-chat { background: linear-gradient(180deg, rgba(12,32,22,0.85), rgba(4,15,10,0.92)); border: 1px solid rgba(200,247,108,0.24); border-radius: 22px; padding: 20px; box-shadow: 0 24px 60px -30px rgba(0,0,0,0.7); }
        .farfadet-messages { min-height: 380px; max-height: 62vh; overflow-y: auto; padding: 12px 4px 20px; margin-bottom: 16px; }
        .farfadet-empty { text-align: center; padding: 32px 20px; }
        .farfadet-chip { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 999px; background: rgba(50,120,80,0.35); border: 1px solid rgba(200,247,108,0.28); color: #e6efd9; font-size: 12px; font-family: "IBM Plex Mono", monospace; transition: all 200ms ease; }
        .farfadet-chip:hover { background: rgba(200,247,108,0.15); border-color: rgba(200,247,108,0.7); }
        .farfadet-msg { display: flex; margin: 10px 0; }
        .farfadet-msg.user { justify-content: flex-end; }
        .farfadet-msg.advisor { justify-content: flex-start; }
        .farfadet-msg-inner { max-width: 78%; padding: 12px 16px; border-radius: 16px; line-height: 1.55; font-size: 14.5px; }
        .farfadet-msg.user .farfadet-msg-inner { background: linear-gradient(135deg, #6ea070, #4b7255); color: #f8f4e5; border-bottom-right-radius: 4px; }
        .farfadet-msg.advisor .farfadet-msg-inner { background: rgba(30,55,38,0.85); color: #f4ead0; border: 1px solid rgba(200,247,108,0.22); border-bottom-left-radius: 4px; }
        .farfadet-msg-inner p { margin: 0 0 8px; }
        .farfadet-msg-inner p:last-child { margin-bottom: 0; }
        .farfadet-msg-inner strong { color: #c8f76c; }
        .farfadet-msg-inner em { color: #d7e8d8; }
        .farfadet-form { display: flex; gap: 10px; align-items: flex-end; border-top: 1px solid rgba(200,247,108,0.16); padding-top: 14px; }
        .farfadet-textarea { flex: 1; resize: none; padding: 12px 14px; border-radius: 14px; background: rgba(8,20,14,0.7); border: 1px solid rgba(200,247,108,0.22); color: #f4ead0; font-family: inherit; font-size: 14.5px; outline: none; transition: border-color 200ms ease; }
        .farfadet-textarea:focus { border-color: rgba(200,247,108,0.7); }
        .farfadet-btn-primary { display: inline-flex; align-items: center; gap: 8px; padding: 12px 18px; border-radius: 12px; background: linear-gradient(135deg, #c8f76c, #8bc34a); color: #0a1a10; font-weight: 600; font-size: 13px; font-family: "IBM Plex Mono", monospace; transition: transform 200ms ease, opacity 200ms ease; }
        .farfadet-btn-primary:hover:not(:disabled) { transform: translateY(-1px); }
        .farfadet-btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
        .farfadet-btn-ghost { display: inline-flex; align-items: center; padding: 12px 14px; border-radius: 12px; background: rgba(180,40,40,0.15); border: 1px solid rgba(255,120,120,0.3); color: #ffb8a8; }

        /* Daily joke banner */
        .farfadet-daily { padding: 20px 22px; border-radius: 20px; background: linear-gradient(180deg, rgba(200,247,108,0.10) 0%, rgba(30,55,38,0.35) 100%); border: 1px solid rgba(200,247,108,0.35); box-shadow: 0 12px 40px -18px rgba(200,247,108,0.25); }
        .farfadet-daily-extra { padding: 14px 16px; border-radius: 14px; background: rgba(12,32,22,0.6); border: 1px dashed rgba(200,247,108,0.3); }
        .farfadet-daily-body { font-family: "Cormorant Garamond", "Georgia", serif; font-size: 18px; line-height: 1.55; color: #f4ead0; }
        .farfadet-daily-body p { margin: 0 0 8px; }
        .farfadet-daily-body p:last-child { margin-bottom: 0; }
        .farfadet-daily-cta { display: inline-flex; align-items: center; gap: 6px; margin-top: 12px; padding: 8px 14px; border-radius: 999px; background: rgba(200,247,108,0.15); border: 1px solid rgba(200,247,108,0.45); color: #c8f76c; font-family: "IBM Plex Mono", monospace; font-size: 11px; font-weight: 600; transition: all 200ms ease; }
        .farfadet-daily-cta:hover { background: rgba(200,247,108,0.25); }
        .farfadet-sub-input { padding: 10px 12px; border-radius: 10px; background: rgba(8,20,14,0.7); border: 1px solid rgba(200,247,108,0.28); color: #f4ead0; font-family: inherit; font-size: 13px; outline: none; min-width: 220px; }
        .farfadet-sub-input:focus { border-color: rgba(200,247,108,0.7); }

        /* Joke card actions */
        .farfadet-actions { display: flex; gap: 6px; margin-top: 10px; padding-top: 8px; border-top: 1px dashed rgba(200,247,108,0.18); }
        .farfadet-action { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 26px; border-radius: 8px; background: rgba(200,247,108,0.08); border: 1px solid rgba(200,247,108,0.2); color: #c8dbcb; transition: all 180ms ease; }
        .farfadet-action:hover:not(:disabled) { background: rgba(200,247,108,0.22); color: #c8f76c; }
        .farfadet-action:disabled { opacity: 0.6; cursor: default; }
        .farfadet-action.active-up { background: rgba(200,247,108,0.35); color: #c8f76c; border-color: rgba(200,247,108,0.65); }
        .farfadet-action.active-down { background: rgba(255,120,120,0.25); color: #ffb8a8; border-color: rgba(255,120,120,0.55); }
      `}</style>
    </div>
  );
}
